// UserCardDropdown — sidebar bottom-card menu (US-043 + US-044).
//
// DS-GAP-DESIGN (US-044): the spec leaves the menu contents to the implementer.
// We keep it intentionally narrow:
//   1. "Settings" link    — navigates to `/settings` (placeholder for Wave 7).
//   2. "Theme" sub-menu   — drop-in light/dark/system selector (radio group).
//   3. "Sign out"         — wired to the same `useSignOut` mutation Wave 4.2
//                            shipped, including the `auth.logout.successToast`
//                            and `auth.logout.errorToast` keys.
//
// We deliberately omit a "Profile" / "Security" split (the spec lists them but
// US-044 is P1-gap; both surfaces collapse into "Settings" until EPIC-14 lands
// the section anchors). Submenu navigation uses `DropdownMenuRadioGroup` so a
// keyboard user can pick a theme without leaving the menu.

import { useNavigate } from '@tanstack/react-router';
import { LogOut, Monitor, Moon, Settings as SettingsIcon, Sun } from 'lucide-react';
import { type ReactNode } from 'react';

import { useSignOut } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  toast,
  useTheme,
  type Theme,
} from '@barel/ui';

export interface UserCardDropdownProps {
  /** Trigger element — typically the user-card button (renders inline). */
  children: ReactNode;
  /** Open state controlled by parent (so the user-card hover ring stays in sync). */
  open?: boolean;
  /** Open-state callback. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * UserCardDropdown — wraps DS `DropdownMenu` for the sidebar bottom card.
 * The trigger element is the user card itself; we render a `Trigger asChild`
 * over whatever the consumer passes so the hit-area + focus ring on the
 * card stay intact.
 */
export function UserCardDropdown({ children, open, onOpenChange }: UserCardDropdownProps) {
  const t = useT('shell');
  const tAuth = useT('auth');
  const navigate = useNavigate();
  const signOut = useSignOut();
  const { theme, setTheme } = useTheme();

  const handleLogout = (): void => {
    signOut.mutate(undefined, {
      onSuccess: () => {
        toast.success({ title: tAuth('logout.successToast') });
        // EPIC-17 US-207 — broadcast logout to the extension content script
        // so it can revoke the bearer + clear `pairedAccount` BEFORE the
        // session cookie disappears. The bridge filters on `event.origin`,
        // and a missed message is harmless (the next /ext/status reconcile
        // will still flip the state in the popup).
        try {
          window.postMessage({ type: 'ls.logout' }, location.origin);
        } catch {
          /* sandboxed iframes / private mode — fine to swallow */
        }
        void navigate({ to: '/auth/login', replace: true });
      },
      onError: () => {
        toast.error({ title: tAuth('logout.errorToast') });
      },
    });
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" sideOffset={8} data-testid="user-card-dropdown">
        <DropdownMenuLabel>{t('userCard.menu.theme')}</DropdownMenuLabel>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid="user-card-theme-sub-trigger">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              {theme === 'light' ? (
                <Sun size={14} aria-hidden="true" />
              ) : theme === 'dark' ? (
                <Moon size={14} aria-hidden="true" />
              ) : (
                <Monitor size={14} aria-hidden="true" />
              )}
              <span>{t('userCard.menu.theme')}</span>
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme} onValueChange={(next) => setTheme(next as Theme)}>
              <DropdownMenuRadioItem value="light">
                {t('userCard.menu.themeLight')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">
                {t('userCard.menu.themeDark')}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">
                {t('userCard.menu.themeSystem')}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={() => {
            // US-043 — settings entry routes to the Profile section by default;
            // the in-section nav lets the user jump elsewhere.
            void navigate({ to: '/settings/$section', params: { section: 'profile' } });
          }}
          data-testid="user-card-settings"
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <SettingsIcon size={14} aria-hidden="true" />
            <span>{t('userCard.menu.settings')}</span>
          </span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onSelect={handleLogout}
          disabled={signOut.isPending}
          data-testid="user-card-logout"
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              color: 'var(--error)',
            }}
          >
            <LogOut size={14} aria-hidden="true" />
            <span>{tAuth('logout.label')}</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
